import React from "react";
import { LsiconRightOutline, MynauiSearch } from "../CollegeIntermediate";

interface FilterOption {
  name: string;
  key: string;
  count: number;
}

interface FilterCategory {
  categoryKey: string;
  categoryName?: string;
  options: FilterOption[];
}

interface Props {
  filters: FilterCategory[];
  selectedFilters: Record<string, string[]>;
  handleFilterChange: (category: string, key: string) => void;
  searchStates: Record<string, string>;
  setSearchStates: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  showMoreStates: Record<string, boolean>;
  setShowMoreStates: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  openAccordions: Record<string, boolean>;
  toggleAccordion: (key: string) => void;
  setSelectedFilters: React.Dispatch<
    React.SetStateAction<Record<string, string[]>>
  >;
  setSearchParams: (params: any) => void;
  noSearchCategories: string[];
}

const DesktopFilterSidebar: React.FC<Props> = ({
  filters,
  selectedFilters,
  handleFilterChange,
  searchStates,
  setSearchStates,
  showMoreStates,
  setShowMoreStates,
  openAccordions,
  toggleAccordion,
  setSelectedFilters,
  setSearchParams,
  noSearchCategories,
}) => {
  return (
    <aside className="bg-[#061958] rounded-md p-4 w-full lg:w-1/4 hidden lg:block">
      <div className="relative flex flex-col gap-1">
        <div className="flex flex-col gap-2">
          <div className="flex justify-between">
            <h3 className="text-lg font-semibold text-white">Filter by</h3>
            {Object.values(selectedFilters).some((arr) => arr.length > 0) && (
              <button
                className="text-white text-sm"
                onClick={() => {
                  setSelectedFilters({});
                  setSearchParams({});
                }}
              >
                Clear All
              </button>
            )}
          </div>
          <hr />
        </div>

        {filters
          .filter(
            (category) =>
              category.options &&
              category.options.length > 0 &&
              category.categoryKey !== "sortBy"
          )
          .map((category) => {
            const searchText = searchStates[category.categoryKey] || "";
            const showAll = showMoreStates[category.categoryKey] || false;
            const filteredOptions = category.options.filter((option) =>
              option.name.toLowerCase().includes(searchText.toLowerCase())
            );

            if (filteredOptions.length === 0) return null;

            const visibleOptions = showAll
              ? filteredOptions
              : filteredOptions.slice(0, 10);

            return (
              <React.Fragment key={category.categoryKey}>
                <div className="flex flex-col gap-3">
                  <button
                    className="flex justify-between items-center w-full py-2 font-medium text-base text-white uppercasetext-left"
                    onClick={() => toggleAccordion(category.categoryKey)}
                  >
                    {category.categoryName}
                    <LsiconRightOutline
                      className={`w-6 h-6 transition-transform ${
                        openAccordions[category.categoryKey]
                          ? "rotate-90"
                          : "rotate-[270deg]"
                      }`}
                    />
                  </button>

                  {openAccordions[category.categoryKey] && (
                    <div className="flex flex-col gap-3 pb-4">
                      {!noSearchCategories.includes(
                        category.categoryKey.toLowerCase()
                      ) && (
                        <div className="flex items-center gap-2 relative">
                          <input
                            type="text"
                            placeholder={`Search by ${category.categoryName}`}
                            value={searchText}
                            onChange={(e) =>
                              setSearchStates((prev) => ({
                                ...prev,
                                [category.categoryKey]: e.target.value,
                              }))
                            }
                            className="w-full pl-3 pr-6 py-1 rounded text-black leading-7"
                          />
                          <button
                            onClick={() => {}}
                            className="text-white absolute right-2"
                          >
                            <MynauiSearch />
                          </button>
                        </div>
                      )}

                      <div
                        className={
                          showAll ? "max-h-[320px] overflow-y-auto" : ""
                        }
                      >
                        <ul className="list-none flex flex-col gap-2 mt-2">
                          {visibleOptions.map((option) => (
                            <li key={option.key}>
                              <label className="flex items-center gap-2 text-white">
                                <span className="relative w-full max-w-5 h-5 block">
                                  <input
                                    type="checkbox"
                                    className="peer appearance-none w-full h-full rounded-sm border border-white bg-transparent checked:bg-white transition-all duration-200"
                                    checked={
                                      selectedFilters[
                                        category.categoryKey
                                      ]?.includes(option.key) || false
                                    }
                                    onChange={() =>
                                      handleFilterChange(
                                        category.categoryKey,
                                        option.key
                                      )
                                    }
                                  />
                                  <svg
                                    width="14"
                                    height="12"
                                    viewBox="0 0 14 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 peer-checked:opacity-100"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12.7554 0.971074C13.1039 1.29115 13.1342 1.8412 12.8231 2.19968L4.76617 11.4812C4.60566 11.6661 4.37612 11.7718 4.13513 11.7718C3.89414 11.7718 3.66459 11.6661 3.50409 11.4812L0.281339 7.76856C-0.0298314 7.41006 0.000443473 6.86002 0.348961 6.54004C0.697467 6.21994 1.23226 6.25103 1.54343 6.60953L4.13513 9.59516L11.561 1.04063C11.8721 0.682153 12.4069 0.651013 12.7554 0.971074Z"
                                      fill="#00B489"
                                    />
                                  </svg>
                                </span>
                                <span>
                                  {option.name.trim()} ({option.count})
                                </span>
                              </label>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {filteredOptions.length > 10 && (
                        <button
                          onClick={() =>
                            setShowMoreStates((prev) => ({
                              ...prev,
                              [category.categoryKey]: !showAll,
                            }))
                          }
                          className="text-green-400 text-sm text-left"
                        >
                          {showAll ? "Show less" : "Show more"}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <hr />
              </React.Fragment>
            );
          })}
      </div>
    </aside>
  );
};

export default DesktopFilterSidebar;
